import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPublicCampaignById } from "../../lib/api/campaign";
import { format, parseISO } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/components/ui/use-toast";
import { Separator } from "@/components/ui/separator";
import {
  Briefcase,
  HelpCircle,
  Sparkles,
  HandHeart,
  Trophy,
  Target,
  MapPin,
  Users,
  Globe,
  Calendar,
  Loader2,
  Star,
} from "lucide-react";
import {
  Location,
  Campaign,
  NPO,
  Opportunity,
  SupportMap,
  CampaignData,
} from "@/types/campaign";

// Helper function to format focus areas for display
const formatFocusArea = (focusArea: string): string => {
  return focusArea.replace(/_/g, " ");
};

// Helper function to format dates
const formatDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), "MMM dd, yyyy");
  } catch (error) {
    return "Invalid date";
  }
};

const CorporateCampaignMicrosite = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>("all");
  const [selectedFormat, setSelectedFormat] = useState<string>("all");
  const [selectedFocusArea, setSelectedFocusArea] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { toast } = useToast();

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        if (!campaignId) {
          setError("Campaign ID is required");
          setLoading(false);
          return;
        }

        const response = await fetchPublicCampaignById(campaignId);
        setCampaignData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching campaign data:", err);
        setError("Failed to load campaign data. Please try again later.");
        setLoading(false);

        toast({
          title: "Error",
          description: "Failed to load campaign data",
          variant: "destructive",
        });
      }
    };

    fetchCampaignData();
  }, [campaignId, toast]);

  // Get unique locations from the campaign data
  const locations = useMemo(() => {
    if (!campaignData) return [];

    const locationSet = new Set<string>();
    campaignData.campaign.meta.locations.forEach((location) => {
      const locationString = `${location.district}, ${location.state}, ${location.country}`;
      locationSet.add(locationString);
    });

    return Array.from(locationSet);
  }, [campaignData]);

  // Get unique formats from opportunities
  const formats = useMemo(() => {
    if (!campaignData) return [];

    const formatSet = new Set<string>();
    campaignData.opportunities.forEach((opportunity) => {
      formatSet.add(opportunity.format);
    });

    return Array.from(formatSet);
  }, [campaignData]);

  // Get unique focus areas from opportunities
  const focusAreas = useMemo(() => {
    if (!campaignData) return [];

    const focusAreaSet = new Set<string>();
    campaignData.opportunities.forEach((opportunity) => {
      focusAreaSet.add(opportunity.focus_area);
    });

    return Array.from(focusAreaSet);
  }, [campaignData]);

  // Filter opportunities based on selected filters and NPO support
  const filteredOpportunities = useMemo(() => {
    if (!campaignData) return [];

    // Get all supported opportunity IDs from the map
    const supportedOpportunityIds = new Set<string>();
    campaignData.map.forEach((mapItem) => {
      // Check if the location filter matches
      const locationMatches =
        selectedLocation === "all" ||
        mapItem.supported_locations.some((loc) => {
          const [country, state, district] = loc.split("-");
          return selectedLocation === `${district}, ${state}, ${country}`;
        });

      if (locationMatches) {
        mapItem.supported_categories.forEach((id) =>
          supportedOpportunityIds.add(id)
        );
      }
    });

    return campaignData.opportunities.filter((opportunity) => {
      // Check if opportunity is supported by at least one NPO for the selected location
      const isSupported = supportedOpportunityIds.has(opportunity.id);

      // Apply format filter
      const formatMatches =
        selectedFormat === "all" || opportunity.format === selectedFormat;

      // Apply focus area filter
      const focusAreaMatches =
        selectedFocusArea === "all" ||
        opportunity.focus_area === selectedFocusArea;
        
      // Apply search filter if search query exists
      const searchQuery_lower = searchQuery.trim().toLowerCase();
      const searchMatches = searchQuery_lower === "" ? true : (
        (opportunity.name || "").toLowerCase().includes(searchQuery_lower) ||
        (opportunity.description || "").toLowerCase().includes(searchQuery_lower) ||
        (opportunity.community_need || "").toLowerCase().includes(searchQuery_lower) ||
        (opportunity.impact || "").toLowerCase().includes(searchQuery_lower) ||
        (opportunity.support_type || "").toLowerCase().includes(searchQuery_lower) ||
        (opportunity.outcome || "").toLowerCase().includes(searchQuery_lower) ||
        (formatFocusArea(opportunity.focus_area || "")).toLowerCase().includes(searchQuery_lower)
      );

      return isSupported && formatMatches && focusAreaMatches && searchMatches;
    });
  }, [campaignData, selectedLocation, selectedFormat, selectedFocusArea, searchQuery]);

  // Get NPOs that support the selected location
  const supportingNPOs = useMemo(() => {
    if (!campaignData) return [];

    if (selectedLocation === "all") {
      return campaignData.npos;
    }

    const supportingNpoIds = new Set<string>();
    campaignData.map.forEach((mapItem) => {
      const locationMatches = mapItem.supported_locations.some((loc) => {
        const [country, state, district] = loc.split("-");
        return selectedLocation === `${district}, ${state}, ${country}`;
      });

      if (locationMatches) {
        supportingNpoIds.add(mapItem.npoId);
      }
    });

    return campaignData.npos.filter((npo) => supportingNpoIds.has(npo.id));
  }, [campaignData, selectedLocation]);

  // Handle opportunity click for booking
  const handleOpportunityClick = (opportunity: Opportunity) => {
    // Get the corporate name from the URL
    const pathParts = window.location.pathname.split("/");
    const corporateNameIndex =
      pathParts.findIndex((part) => part === "corporate") + 1;
    const corporateName = pathParts[corporateNameIndex] || "default";

    // Navigate to the booking page without query parameters
    navigate(
      `/corporate/${corporateName}/events/${campaignId}/book/${opportunity.id}`
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center p-8"
        >
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-blue-800 mb-2">
            Loading Campaign
          </h2>
          <p className="text-gray-600">
            Please wait while we prepare your volunteering opportunities...
          </p>
        </motion.div>
      </div>
    );
  }

  if (error || !campaignData) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center"
        >
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Oops! Something went wrong
          </h1>
          <p className="text-gray-700 mb-6">
            {error || "Campaign data not available"}
          </p>
          <Button
            className="bg-blue-600 hover:bg-blue-700 transition-colors"
            onClick={() => window.history.back()}
          >
            Go Back
          </Button>
        </motion.div>
      </div>
    );
  }

  const { campaign, npos } = campaignData;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-blue-200 opacity-20 blur-3xl"></div>
        <div className="absolute top-40 right-20 w-80 h-80 rounded-full bg-purple-200 opacity-20 blur-3xl"></div>
        <div className="absolute bottom-20 left-1/4 w-96 h-96 rounded-full bg-green-200 opacity-20 blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4 z-10"
      >
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="max-w-full md:max-w-3/5 mb-8 md:mb-0"
            >
              <h1 className="text-5xl font-bold mb-6 leading-tight">
                {campaign.name}
              </h1>
              <div className="relative">
                <p
                  className="text-xl mb-8 leading-relaxed text-blue-100 hover:cursor-help"
                  title={campaign.meta.description}
                >
                  {campaign.meta.description.length > 300
                    ? `${campaign.meta.description.substring(0, 300)}...`
                    : campaign.meta.description}
                </p>
              </div>
              <div className="flex items-center space-x-4 mb-6 bg-blue-700/50 p-4 rounded-lg backdrop-blur-sm">
                <div className="flex items-center">
                  <Calendar className="mr-2 text-blue-200" />
                  <span className="font-medium">
                    {formatDate(campaign.active_from)} -{" "}
                    {formatDate(campaign.active_to)}
                  </span>
                </div>
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                className="inline-block"
              >
                <Badge className="text-lg px-4 py-2 bg-white text-blue-800 hover:bg-blue-50">
                  Theme: {campaign.meta.theme}
                </Badge>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="bg-white/90 backdrop-blur-sm text-gray-800 p-6 rounded-lg shadow-xl max-w-full md:max-w-2/5"
            >
              <h3 className="text-xl font-bold mb-4 text-blue-800 flex items-center">
                <Star className="mr-2 text-yellow-500" /> Campaign Goals
              </h3>
              <div className="flex flex-col space-y-4">
                {campaign.meta.goals.map((goal, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start bg-blue-50 p-3 rounded-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
                  >
                    <Target className="mr-3 mt-1 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">{goal}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="container mx-auto max-w-7xl py-12 px-4 relative z-10">
        {/* Opportunities Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <h2 className="text-3xl font-bold mb-6 md:mb-0 text-blue-800 border-b-2 border-blue-200 pb-2 inline-block">
              Volunteer Opportunities
            </h2>
            <div className="flex flex-col space-y-3 w-full bg-white/80 backdrop-blur-sm p-4 rounded-lg shadow-sm">
              <div className="flex flex-col space-y-1 w-full">
                <label className="text-sm font-medium text-gray-600">
                  Search Opportunities
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, description, impact..."
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors w-full"
                />
              </div>
              <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 w-full">
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">
                  Location
                </label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="all">All Locations</option>
                  {locations.map((location, index) => (
                    <option key={index} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">
                  Format
                </label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="all">All Formats</option>
                  {formats.map((format, index) => (
                    <option key={index} value={format}>
                      {formatFocusArea(format)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">
                  Focus Area
                </label>
                <select
                  value={selectedFocusArea}
                  onChange={(e) => setSelectedFocusArea(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="all">All Focus Areas</option>
                  {focusAreas.map((area, index) => (
                    <option key={index} value={area}>
                      {formatFocusArea(area)}
                    </option>
                  ))}
                </select>
              </div>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {filteredOpportunities.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                <Card className="p-8 text-center bg-white/90 backdrop-blur-sm border-none shadow-lg">
                  <h3 className="text-xl font-bold mb-4 text-blue-800">
                    No opportunities available
                  </h3>
                  <p className="mb-6 text-gray-600">
                    There are no opportunities available for the selected
                    filters.
                  </p>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 transition-colors"
                    onClick={() => {
                      setSelectedLocation("all");
                      setSelectedFormat("all");
                      setSelectedFocusArea("all");
                      setSearchQuery("");
                    }}
                  >
                    Reset Filters
                  </Button>
                </Card>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredOpportunities.map((opportunity, index) => (
                  <motion.div
                    key={opportunity.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                    className="h-full"
                  >
                    <Card
                      className="overflow-hidden border-none shadow-lg h-full bg-white/90 backdrop-blur-sm flex flex-col cursor-pointer"
                      onClick={() => handleOpportunityClick(opportunity)}
                    >
                      <div className="relative h-48 overflow-hidden">
                        {opportunity.image_url ? (
                          <img
                            src={opportunity.image_url}
                            alt={opportunity.name}
                            className="w-full h-full object-cover transition-transform hover:scale-110 duration-700"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-full h-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-500">
                            <Briefcase className="w-16 h-16" />
                          </div>
                        )}
                        <div className="absolute top-0 right-0 p-2">
                          <Badge className="bg-blue-600 hover:bg-blue-700">
                            {formatFocusArea(opportunity.format)}
                          </Badge>
                        </div>
                      </div>
                      <CardContent className="pt-6 flex-grow">
                        <h3 className="text-xl font-bold mb-3 text-blue-800 truncate">
                          {opportunity.name}
                        </h3>

                        <div className="flex flex-wrap gap-2 mb-4">
                          <Badge className="flex items-center bg-blue-100 text-blue-800 hover:bg-blue-200">
                            <Target className="w-3 h-3 mr-1" />
                            <span className="text-xs">
                              {formatFocusArea(opportunity.focus_area)}
                            </span>
                          </Badge>
                          <Badge className="flex items-center bg-red-100 text-red-800 hover:bg-red-200">
                            <MapPin className="w-3 h-3 mr-1" />
                            <span className="text-xs">
                              {opportunity.location}
                            </span>
                          </Badge>
                        </div>

                        <Accordion
                          type="single"
                          collapsible
                          className="mb-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <AccordionItem
                            value="community-need"
                            className="border-none bg-blue-50 rounded-lg"
                          >
                            <AccordionTrigger className="py-3 px-4 hover:no-underline">
                              <div className="flex items-center">
                                <HelpCircle className="mr-2 text-blue-600" />
                                <span className="font-bold">
                                  Community Need
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <p className="text-gray-700">
                                {opportunity.community_need}
                              </p>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>

                        <Separator className="my-4" />

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-yellow-50 p-3 rounded-lg">
                            <div className="flex items-center mb-2">
                              <Sparkles className="mr-2 text-yellow-600" />
                              <span className="font-bold text-sm">Impact</span>
                            </div>
                            <p className="text-sm line-clamp-2 text-gray-700">
                              {opportunity.impact}
                            </p>
                          </div>
                          <div className="bg-red-50 p-3 rounded-lg">
                            <div className="flex items-center mb-2">
                              <HandHeart className="mr-2 text-red-600" />
                              <span className="font-bold text-sm">
                                Support Type
                              </span>
                            </div>
                            <p className="text-sm line-clamp-2 text-gray-700">
                              {opportunity.meta.volunteers_do}
                            </p>
                          </div>
                          <div className="bg-green-50 p-3 rounded-lg">
                            <div className="flex items-center mb-2">
                              <Trophy className="mr-2 text-green-600" />
                              <span className="font-bold text-sm">Outcome</span>
                            </div>
                            <p className="text-sm line-clamp-2 text-gray-700">
                              {opportunity.outcome}
                            </p>
                          </div>
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <div className="flex items-center mb-2">
                              <Users className="mr-2 text-purple-600" />
                              <span className="font-bold text-sm">
                                Beneficiaries
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {opportunity.beneficiary_types
                                .slice(0, 2)
                                .map((type, index) => (
                                  <Badge
                                    key={index}
                                    variant="outline"
                                    className="text-xs bg-white"
                                  >
                                    {formatFocusArea(type)}
                                  </Badge>
                                ))}
                              {opportunity.beneficiary_types.length > 2 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-white"
                                >
                                  +{opportunity.beneficiary_types.length - 2}{" "}
                                  more
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {opportunity.relevant_un_day.length > 0 && (
                          <>
                            <Separator className="my-4" />
                            <div className="bg-blue-50 p-3 rounded-lg mb-4">
                              <div className="flex items-center mb-2">
                                <Globe className="mr-2 text-blue-600" />
                                <span className="font-bold text-sm">
                                  Relevant UN Days
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {opportunity.relevant_un_day
                                  .slice(0, 2)
                                  .map((day, index) => (
                                    <Badge
                                      key={index}
                                      variant="outline"
                                      className="text-xs bg-white"
                                    >
                                      {day}
                                    </Badge>
                                  ))}
                                {opportunity.relevant_un_day.length > 2 && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs bg-white"
                                  >
                                    +{opportunity.relevant_un_day.length - 2}{" "}
                                    more
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </CardContent>
                      <CardFooter className="pt-0">
                        <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105">
                          <Calendar className="mr-2" />
                          Book This Opportunity
                        </Button>
                      </CardFooter>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Campaign Locations Section */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-8 text-blue-800 border-b-2 border-blue-200 pb-2 inline-block">
            Campaign Locations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaign.meta.locations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                whileHover={{
                  y: -5,
                  boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.1)",
                }}
                className="transition-all duration-300"
              >
                <Card className="overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-3">
                      <div className="bg-red-100 p-2 rounded-full mr-3">
                        <MapPin className="text-red-500" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">
                        {location.district}, {location.state}
                      </h3>
                    </div>
                    <p className="mb-3 text-gray-600">{location.country}</p>
                    {location.meta?.details && (
                      <p className="text-gray-600 mb-3 bg-gray-50 p-3 rounded-md">
                        {location.meta.details}
                      </p>
                    )}
                    {location.meta?.volunteers && (
                      <div className="flex items-center bg-blue-50 p-3 rounded-md">
                        <Users className="mr-2 text-blue-600" />
                        <p className="font-bold text-blue-800">
                          {location.meta.volunteers} volunteers needed
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* NPO Partners Section */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-8 text-blue-800 border-b-2 border-blue-200 pb-2 inline-block">
            NPO Partners
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {npos.map((npo, index) => (
              <motion.div
                key={npo.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 + index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
                className="transition-all duration-300"
              >
                <Card className="overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-sm">
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start">
                      <div className="bg-gray-100 rounded-xl p-2 mb-4 sm:mb-0 mr-0 sm:mr-6">
                        <img
                          src={npo.logo_url}
                          alt={npo.org_name}
                          className="w-24 h-24 object-contain rounded-md"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2 text-blue-800">
                          {npo.org_name}
                        </h3>
                        <p className="line-clamp-3 mb-4 text-gray-600">
                          {npo.description}
                        </p>
                        <div className="flex space-x-4">
                          <Button
                            variant="link"
                            size="sm"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                            asChild
                          >
                            <a
                              href={npo.website}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Visit Website
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Supporting NPOs for selected location */}
        {selectedLocation !== "all" && supportingNPOs.length > 0 && (
          <motion.div
            className="mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-blue-800 border-b-2 border-blue-200 pb-2 inline-block">
              NPOs Supporting {selectedLocation}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {supportingNPOs.map((npo, index) => (
                <motion.div
                  key={npo.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2 + index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -5 }}
                  className="transition-all duration-300"
                >
                  <Card className="overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-sm">
                    <CardContent className="pt-6">
                      <div className="flex items-center mb-4">
                        <div className="bg-blue-50 p-2 rounded-full mr-4">
                          <img
                            src={npo.logo_url}
                            alt={npo.org_name}
                            className="w-12 h-12 object-contain rounded-md"
                          />
                        </div>
                        <h3 className="text-lg font-bold text-blue-800">
                          {npo.org_name}
                        </h3>
                      </div>
                      <p className="line-clamp-2 mb-4 text-gray-600">
                        {npo.description}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-blue-500 text-blue-600 hover:bg-blue-50 transition-colors"
                        asChild
                      >
                        <a
                          href={npo.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Learn More
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-100 py-8">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p>
              &copy; {new Date().getFullYear()} CauseConnect. All rights
              reserved.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <Button variant="link">Privacy Policy</Button>
              <Button variant="link">Terms of Service</Button>
              <Button variant="link">Contact Us</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateCampaignMicrosite;
